import {
  Button,
  Flex,
  Input,
  Popover,
  Portal,
  SegmentGroup,
  SegmentGroupValueChangeDetails,
  Text,
} from '@chakra-ui/react';
import { ChangeEvent, Menu, MenuItem } from '@components/common/Menu';
import { formatPrice } from '@features/prediction-markets/fakeMarketData';
import { YesNo } from '@features/prediction-markets/OutcomeRow';
import { MarketOutcome } from '@features/prediction-markets/types';
import useControlledPopover from 'hooks/useControlledPopover';
import { memo, useCallback, useMemo, useState } from 'react';
import { LuChevronDown } from 'react-icons/lu';

type OrderType = 'limit' | 'market';

const ORDER_TYPE_LABELS: Record<OrderType, string> = { limit: 'Limit', market: 'Market' };
const SHARE_QUICK_DELTAS = [-100, -10, 10, 100] as const;

// --- OrderTypePicker ---

interface OrderTypePickerProps {
  orderType: OrderType;
  onOrderTypeChange: (type: OrderType) => void;
}

export const OrderTypePicker = memo(({ orderType, onOrderTypeChange }: OrderTypePickerProps) => {
  const { open, onToggle, onClose, triggerRef, contentRef } = useControlledPopover();

  const handleChange = useCallback(
    (e: ChangeEvent) => {
      onOrderTypeChange(e.value as OrderType);
      onClose();
    },
    [onOrderTypeChange, onClose],
  );

  return (
    <Popover.Root open={open} onOpenChange={onToggle} lazyMount>
      <Popover.Trigger asChild>
        <Flex
          ref={triggerRef}
          alignItems="center"
          gap={1}
          cursor="pointer"
          _hover={{ color: 'text.primary' }}
        >
          <Text fontSize="xs" color="text.secondary">
            {ORDER_TYPE_LABELS[orderType]}
          </Text>
          <LuChevronDown size={12} />
        </Flex>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            ref={contentRef}
            borderRadius="lg"
            bgColor="surface.background"
            p={1}
            minW="100px"
            w="auto"
          >
            <Menu value={orderType} showSelected onChange={handleChange} fontSize="xs">
              <MenuItem value="limit">Limit</MenuItem>
              <MenuItem value="market">Market</MenuItem>
            </Menu>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
});
OrderTypePicker.displayName = 'OrderTypePicker';

// --- BuySellSegment ---

interface BuySellSegmentProps {
  side: 'buy' | 'sell';
  onSideChange: (side: 'buy' | 'sell') => void;
}

export const BuySellSegment = memo(({ side, onSideChange }: BuySellSegmentProps) => {
  const handleChange = useCallback(
    (e: SegmentGroupValueChangeDetails) => {
      if (e.value != null) onSideChange(e.value as 'buy' | 'sell');
    },
    [onSideChange],
  );

  return (
    <SegmentGroup.Root value={side} onValueChange={handleChange} size="xs">
      <SegmentGroup.Indicator bg="surface.accent" />
      <SegmentGroup.Item value="buy" cursor="pointer">
        <SegmentGroup.ItemText>Buy</SegmentGroup.ItemText>
        <SegmentGroup.ItemHiddenInput />
      </SegmentGroup.Item>
      <SegmentGroup.Item value="sell" cursor="pointer">
        <SegmentGroup.ItemText>Sell</SegmentGroup.ItemText>
        <SegmentGroup.ItemHiddenInput />
      </SegmentGroup.Item>
    </SegmentGroup.Root>
  );
});
BuySellSegment.displayName = 'BuySellSegment';

// --- YesNoButtons ---

interface YesNoButtonsProps {
  outcome: MarketOutcome;
  yesNo: YesNo;
  onYesNoChange: (value: YesNo) => void;
}

export const YesNoButtons = memo(({ outcome, yesNo, onYesNoChange }: YesNoButtonsProps) => {
  const handleYes = useCallback(() => onYesNoChange('yes'), [onYesNoChange]);
  const handleNo = useCallback(() => onYesNoChange('no'), [onYesNoChange]);

  return (
    <Flex gap={2}>
      <Button
        flex={1}
        size="xs"
        bg={yesNo === 'yes' ? 'green.500' : 'green.100'}
        color={yesNo === 'yes' ? 'white' : 'green.600'}
        _hover={{ bg: 'green.500', color: 'white' }}
        _dark={{
          bg: yesNo === 'yes' ? 'green.500' : 'green.900/30',
          color: yesNo === 'yes' ? 'white' : 'green.300',
          _hover: { bg: 'green.500', color: 'white' },
        }}
        borderRadius="lg"
        onClick={handleYes}
      >
        Yes {formatPrice(outcome.yesPrice)}
      </Button>
      <Button
        flex={1}
        size="xs"
        bg={yesNo === 'no' ? 'red.500' : 'red.100'}
        color={yesNo === 'no' ? 'white' : 'red.600'}
        _hover={{ bg: 'red.500', color: 'white' }}
        _dark={{
          bg: yesNo === 'no' ? 'red.500' : 'red.900/30',
          color: yesNo === 'no' ? 'white' : 'red.300',
          _hover: { bg: 'red.500', color: 'white' },
        }}
        borderRadius="lg"
        onClick={handleNo}
      >
        No {formatPrice(outcome.noPrice)}
      </Button>
    </Flex>
  );
});
YesNoButtons.displayName = 'YesNoButtons';

// --- LimitPriceControl ---

interface LimitPriceControlProps {
  limitPrice: string;
  onInc: () => void;
  onDec: () => void;
}

export const LimitPriceControl = memo(({ limitPrice, onInc, onDec }: LimitPriceControlProps) => (
  <Flex direction="column" gap={1}>
    <Text fontSize="sm" fontWeight="medium" color="text.primary">
      Limit Price
    </Text>
    <Flex alignItems="center" gap={2}>
      <Button size="xs" variant="secondaryOutline" borderRadius="md" onClick={onDec}>
        -
      </Button>
      <Text
        fontSize="sm"
        fontWeight="medium"
        textAlign="center"
        flex={1}
        fontVariantNumeric="tabular-nums"
        color="text.primary"
      >
        {limitPrice}¢
      </Text>
      <Button size="xs" variant="secondaryOutline" borderRadius="md" onClick={onInc}>
        +
      </Button>
    </Flex>
  </Flex>
));
LimitPriceControl.displayName = 'LimitPriceControl';

// --- SharesControl ---

interface SharesControlProps {
  shares: number;
  onSharesChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onQuickShares: (delta: number) => () => void;
}

export const SharesControl = memo(
  ({ shares, onSharesChange, onQuickShares }: SharesControlProps) => (
    <Flex direction="column" gap={1}>
      <Text fontSize="sm" fontWeight="medium" color="text.primary">
        Shares
      </Text>
      <Input
        size="sm"
        type="number"
        value={shares}
        onChange={onSharesChange}
        borderRadius="lg"
        textAlign="end"
        borderColor="border.light"
        bg="surface.background"
        color="text.primary"
      />
      <Flex gap={1} justifyContent="flex-end" pt={1}>
        {SHARE_QUICK_DELTAS.map((delta) => (
          <Button
            key={delta}
            size="2xs"
            variant="secondaryOutline"
            borderRadius="md"
            fontSize="xs"
            px={2}
            onClick={onQuickShares(delta)}
          >
            {delta > 0 ? `+${delta}` : delta}
          </Button>
        ))}
      </Flex>
    </Flex>
  ),
);
SharesControl.displayName = 'SharesControl';

// --- TradeSummary ---

interface TradeSummaryProps {
  total: number;
  toWin: number;
}

export const TradeSummary = memo(({ total, toWin }: TradeSummaryProps) => (
  <Flex direction="column" gap={1} borderTopWidth="1px" borderColor="border.light" pt={3}>
    <Flex justifyContent="space-between">
      <Text fontSize="sm" color="text.secondary">
        Total
      </Text>
      <Text fontSize="sm" fontWeight="semibold" color="text.primary">
        GHS{total.toFixed(2)}
      </Text>
    </Flex>
    <Flex justifyContent="space-between">
      <Text fontSize="sm" color="text.secondary">
        To win
      </Text>
      <Text fontSize="sm" fontWeight="semibold" color="green.500">
        GHS{toWin.toFixed(2)}
      </Text>
    </Flex>
  </Flex>
));
TradeSummary.displayName = 'TradeSummary';

// --- TradeButton ---

export const TradeButton = memo(() => (
  <Button
    w="full"
    size="xs"
    bg="surface.inverse"
    color="text.inverse"
    _hover={{ opacity: 0.9 }}
    borderRadius="lg"
  >
    Trade
  </Button>
));
TradeButton.displayName = 'TradeButton';

// --- useTradeForm (shared hook) ---

export function useTradeForm() {
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [orderType, setOrderType] = useState<OrderType>('limit');
  const [limitPrice, setLimitPrice] = useState('0.0');
  const [shares, setShares] = useState(0);

  const handleSideChange = useCallback((value: 'buy' | 'sell') => {
    setSide(value);
  }, []);

  const handleOrderTypeChange = useCallback((type: OrderType) => {
    setOrderType(type);
  }, []);

  const handleLimitPriceInc = useCallback(() => {
    setLimitPrice((p) => (parseFloat(p) + 0.1).toFixed(1));
  }, []);

  const handleLimitPriceDec = useCallback(() => {
    setLimitPrice((p) => Math.max(0, parseFloat(p) - 0.1).toFixed(1));
  }, []);

  const handleSharesChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    setShares(isNaN(val) ? 0 : val);
  }, []);

  const handleQuickShares = useCallback(
    (delta: number) => () => {
      setShares((prev) => Math.max(0, prev + delta));
    },
    [],
  );

  const computeSummary = useCallback(
    (outcome: MarketOutcome | null, yesNo: YesNo) => {
      if (outcome == null) return { total: 0, toWin: 0 };
      const selectedPrice = yesNo === 'yes' ? outcome.yesPrice : outcome.noPrice;
      const total = shares * selectedPrice;
      const toWin = shares > 0 ? shares - total : 0;
      return { total, toWin };
    },
    [shares],
  );

  return useMemo(
    () => ({
      side,
      orderType,
      limitPrice,
      shares,
      handleSideChange,
      handleOrderTypeChange,
      handleLimitPriceInc,
      handleLimitPriceDec,
      handleSharesChange,
      handleQuickShares,
      computeSummary,
    }),
    [
      side,
      orderType,
      limitPrice,
      shares,
      handleSideChange,
      handleOrderTypeChange,
      handleLimitPriceInc,
      handleLimitPriceDec,
      handleSharesChange,
      handleQuickShares,
      computeSummary,
    ],
  );
}
